import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import "./style.css"

import { RouterProvider, createRouter } from "@tanstack/react-router"
import { routeTree } from "./routeTree.gen"
import useUserStore from "./store/userStore"
import { ThemeProvider } from "./contexts/theme"
import { PostHogProvider } from "posthog-js/react"
import * as Sentry from "@sentry/react"
import { PostHogConfig } from "posthog-js"

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultNotFoundComponent: () => (
    <div className="mt-6 flex-col pb-6 sm:w-full">
      <div className="rounded-lg border-2 p-6 text-lg md:w-1/2">
        <h4 className="mb-2 text-xl font-bold text-gray-800">404 Error</h4>
        We were unable to find the page you are looking for.
      </div>
    </div>
  ),
  context: {
    session: null,
    getSession: () => Promise.resolve(null),
    orgId: null,
    getUser: () => Promise.resolve(null),
    role: null,
    permissions: [],
  },
})

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  enabled: import.meta.env.VITE_IS_PROD,
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const App = () => {
  const { session, getSession, organisation, getUser, role, permissions } =
    useUserStore()
  return (
    <ThemeProvider>
      <RouterProvider
        router={router}
        context={{
          session,
          getSession,
          orgId: organisation?.id,
          getUser,
          role,
          permissions,
        }}
      />
    </ThemeProvider>
  )
}
const posthogOptions: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2025-05-24",
}

const ProductionPostHogProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  if (!import.meta.env.VITE_PUBLIC_POSTHOG_KEY) return <>{children}</>
  return (
    <PostHogProvider
      options={posthogOptions}
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
    >
      {children}
    </PostHogProvider>
  )
}

const rootElement = document.getElementById("root")
if (!rootElement) throw new Error("No root element found")
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement)
  root.render(
    <StrictMode>
      <ProductionPostHogProvider>
        <App />
      </ProductionPostHogProvider>
    </StrictMode>,
  )
}
